/* ------------------------------------------------------------------
   header-nav —— 滚动状态机 + 桌面浮窗交互
   自研而非复用 xo-sticky：新状态机需要 4 个输入（是否首页 / 是否在顶部 /
   是否 hover / 是否有浮窗展开），xo-sticky 只提供其中一个。
   详见 spec.md §6。
   ------------------------------------------------------------------ */

(function () {
  'use strict';

  var TOP_THRESHOLD = 8;
  var DIR_THRESHOLD = 4;
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class HeaderNav extends HTMLElement {
    constructor() {
      super();
      this.lastScrollY = 0;
      this.headerOffset = 0;
      this.ticking = false;
      this.activeIndex = null;
      this.drawerOpen = false;
      this.activePanel = null;
      this.flyoutCloseTimer = null;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.refreshAnnouncementHeight = this.refreshAnnouncementHeight.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onBarPointerOver = this.onBarPointerOver.bind(this);
      this.onBarPointerLeave = this.onBarPointerLeave.bind(this);
      this.onFlyoutRegionEnter = this.onFlyoutRegionEnter.bind(this);
      this.onFlyoutRegionLeave = this.onFlyoutRegionLeave.bind(this);
      this.onFlyoutWheel = this.onFlyoutWheel.bind(this);
      this.flyoutWheelListenerOptions = { capture: true, passive: false };
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onDrawerToggle = this.onDrawerToggle.bind(this);
      this.onDrawerClick = this.onDrawerClick.bind(this);
    }

    connectedCallback() {
      // 循环外缓存 DOM 查询
      this.bar = this.querySelector('.header-nav__bar');
      this.announcement = document.querySelector('[id*="announcement-bar"]');
      this.announcementOriginalMinHeight = this.announcement ? this.announcement.style.minHeight : '';
      this.flyoutRegion = this.querySelector('.header-nav__flyouts');
      this.flyouts = Array.from(this.querySelectorAll('.header-nav__flyout'));
      this.triggers = Array.from(this.querySelectorAll('.header-nav__item[data-has-flyout]'));
      this.drawer = this.querySelector('[data-drawer]');
      this.drawerToggle = this.querySelector('[data-drawer-toggle]');
      this.rootPanel = this.querySelector('[data-drawer-panel="root"]');
      this.subPanels = Array.from(this.querySelectorAll('.header-nav-drawer__panel--sub'));

      this.lastScrollY = window.scrollY;
      this.refreshAnnouncementHeight();
      this.refreshHeaderOffset();
      this.applyScrollState(true);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('keydown', this.onKeyDown);

      if (this.bar && this.flyouts.length) {
        this.bar.addEventListener('pointerover', this.onBarPointerOver);
        this.bar.addEventListener('pointerleave', this.onBarPointerLeave);
      }

      if (this.flyoutRegion) {
        this.flyoutRegion.addEventListener('pointerenter', this.onFlyoutRegionEnter);
        this.flyoutRegion.addEventListener('pointerleave', this.onFlyoutRegionLeave);
        this.flyoutRegion.addEventListener('wheel', this.onFlyoutWheel, this.flyoutWheelListenerOptions);
      }

      if (this.drawerToggle && this.drawer) {
        this.drawerToggle.addEventListener('click', this.onDrawerToggle);
      }

      if (this.drawer) {
        this.drawer.addEventListener('click', this.onDrawerClick);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('keydown', this.onKeyDown);

      if (this.bar) {
        this.bar.removeEventListener('pointerover', this.onBarPointerOver);
        this.bar.removeEventListener('pointerleave', this.onBarPointerLeave);
      }

      if (this.flyoutRegion) {
        this.flyoutRegion.removeEventListener('pointerenter', this.onFlyoutRegionEnter);
        this.flyoutRegion.removeEventListener('pointerleave', this.onFlyoutRegionLeave);
        this.flyoutRegion.removeEventListener('wheel', this.onFlyoutWheel, this.flyoutWheelListenerOptions);
      }

      if (this.drawerToggle) {
        this.drawerToggle.removeEventListener('click', this.onDrawerToggle);
      }

      if (this.drawer) {
        this.drawer.removeEventListener('click', this.onDrawerClick);
      }

      this.cancelFlyoutClose();

      if (this.announcement) {
        this.announcement.style.minHeight = this.announcementOriginalMinHeight;
      }

      // 组件被移除时（Theme Editor 重载）必须解除滚动锁，否则页面永久锁死
      document.body.classList.remove('header-nav-drawer-open');
    }

    /* ── 滚动状态 ───────────────────────────────────────────────── */

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.applyScrollState(false);
        this.ticking = false;
      });
    }

    onResize() {
      this.refreshAnnouncementHeight();
      this.refreshHeaderOffset();
      this.applyScrollState(true);
    }

    refreshAnnouncementHeight() {
      if (!this.announcement) return;

      var desktopMin = Number(this.dataset.desktopMin || 900);
      var isMobileLayout = window.innerWidth < desktopMin;

      if (isMobileLayout) {
        var mobileAnnouncementHeight = parseFloat(
          getComputedStyle(this).getPropertyValue('--nav-announcement-height-m')
        );
        if (Number.isFinite(mobileAnnouncementHeight)) {
          this.announcement.style.minHeight = mobileAnnouncementHeight + 'px';
        }
      } else {
        this.announcement.style.minHeight = this.announcementOriginalMinHeight;
        this.style.removeProperty('--nav-announcement-height');
        return;
      }

      var announcementHeight = this.announcement.getBoundingClientRect().height;
      this.style.setProperty('--nav-announcement-height', announcementHeight + 'px');
    }

    refreshHeaderOffset() {
      this.headerOffset = this.getBoundingClientRect().top + window.scrollY;
    }

    applyScrollState(isInitial) {
      var currentY = window.scrollY;
      var atTop = currentY <= TOP_THRESHOLD;
      var shouldFix = this.getAttribute('data-sticky') !== 'none' && currentY >= this.headerOffset - 1;

      this.setAttribute('data-at-top', atTop ? 'true' : 'false');
      this.setAttribute('data-header-fixed', shouldFix ? 'true' : 'false');

      if (isInitial) {
        this.setAttribute('data-scroll-dir', 'up');
        this.lastScrollY = currentY;
        return;
      }

      var delta = currentY - this.lastScrollY;

      if (Math.abs(delta) >= DIR_THRESHOLD) {
        this.setAttribute('data-scroll-dir', delta > 0 ? 'down' : 'up');
        this.lastScrollY = currentY;
      }
    }

    /* ── hover / focus 状态（决定透明态是否退出）─────────────────── */

    onPointerEnter() {
      this.setAttribute('data-hover', 'true');
    }

    onPointerLeave() {
      this.setAttribute('data-hover', 'false');
    }

    onFocusIn(event) {
      this.setAttribute('data-hover', 'true');

      var item = event.target.closest('.header-nav__item[data-has-flyout]');
      if (item) {
        this.openFlyout(item.dataset.itemIndex);
      } else if (!event.target.closest('.header-nav__flyout')) {
        this.closeFlyout();
      }
    }

    onFocusOut() {
      if (!this.contains(document.activeElement)) {
        this.setAttribute('data-hover', 'false');
        this.closeFlyout();
      }
    }

    /* ── 浮窗 ───────────────────────────────────────────────────── */

    // 用 pointerover 委托而非逐项 pointerenter：
    // 移到浮窗上要保持展开，移到 logo / 图标区要收起，一处判断覆盖全部路径。
    onBarPointerOver(event) {
      var item = event.target.closest('.header-nav__item[data-has-flyout]');
      if (item) {
        this.cancelFlyoutClose();
        this.openFlyout(item.dataset.itemIndex);
        return;
      }

      if (event.target.closest('.header-nav__flyout')) {
        this.cancelFlyoutClose();
        return;
      }

      if (event.target.closest('.header-nav__flyouts')) {
        this.cancelFlyoutClose();
        this.setAttribute('data-hover', 'true');
        return;
      }

      this.closeFlyout();
    }

    onBarPointerLeave(event) {
      if (event.relatedTarget && this.bar && this.bar.contains(event.relatedTarget)) return;
      this.scheduleFlyoutClose();
    }

    onFlyoutRegionEnter() {
      this.cancelFlyoutClose();
      this.setAttribute('data-hover', 'true');
    }

    onFlyoutRegionLeave(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.setAttribute('data-hover', 'false');
      this.scheduleFlyoutClose();
    }

    onFlyoutWheel(event) {
      var target = event.target;
      var panel = target && target.closest ? target.closest('.header-nav__flyout') : null;
      if (!panel || panel.hidden || event.deltaY === 0) return;

      var hasVerticalOverflow = panel.scrollHeight > panel.clientHeight;
      if (!hasVerticalOverflow) {
        event.preventDefault();
        return;
      }

      var atTop = panel.scrollTop <= 0;
      var atBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 1;
      if ((event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) {
        event.preventDefault();
      }
    }

    onKeyDown(event) {
      if (event.key === 'Tab' && this.drawerOpen) {
        this.trapFocus(event);
        return;
      }

      if (event.key !== 'Escape') return;

      if (this.drawerOpen) {
        this.closeDrawer();
        if (this.drawerToggle) this.drawerToggle.focus();
        return;
      }

      if (this.activeIndex === null) return;

      var activeIndex = this.activeIndex;
      this.closeFlyout();

      var trigger = this.triggers.find((el) => el.dataset.itemIndex === activeIndex);
      var link = trigger && trigger.querySelector('.header-nav__link');
      if (link) link.focus();
    }

    /* ── 移动抽屉 ───────────────────────────────────────────────── */

    onDrawerToggle() {
      if (this.drawerOpen) {
        this.closeDrawer();
      } else {
        this.openDrawer();
      }
    }

    openDrawer() {
      if (!this.drawer) return;

      this.drawerOpen = true;
      this.drawer.hidden = false;
      this.setAttribute('data-drawer-open', 'true');
      document.body.classList.add('header-nav-drawer-open');

      if (this.drawerToggle) this.drawerToggle.setAttribute('aria-expanded', 'true');

      var firstFocusable = this.drawer.querySelector(FOCUSABLE);
      if (firstFocusable) firstFocusable.focus();
    }

    closeDrawer() {
      if (!this.drawer) return;

      this.drawerOpen = false;
      this.drawer.hidden = true;
      this.setAttribute('data-drawer-open', 'false');
      document.body.classList.remove('header-nav-drawer-open');

      if (this.drawerToggle) this.drawerToggle.setAttribute('aria-expanded', 'false');

      // 关闭抽屉时回到一级页，下次打开不会停在上次的二级页
      this.closeSubpage();
    }

    /* ── 二级页 ─────────────────────────────────────────────────── */

    onDrawerClick(event) {
      var opener = event.target.closest('[data-subpage-open]');
      if (opener) {
        this.openSubpage(opener.dataset.subpageOpen, opener);
        return;
      }

      if (event.target.closest('[data-subpage-close]')) {
        this.closeSubpage();
      }
    }

    openSubpage(index, opener) {
      if (index === undefined || index === null) return;

      var panel = this.subPanels.find((el) => el.dataset.drawerPanel === index);
      // 面板不存在时静默返回（Phase 4 阶段二级页尚未接入时的保护）
      if (!panel) return;

      this.activePanel = index;
      panel.hidden = false;
      if (this.rootPanel) this.rootPanel.hidden = true;
      if (opener) opener.setAttribute('aria-expanded', 'true');

      this.drawer.scrollTop = 0;

      var back = panel.querySelector('[data-subpage-close]');
      if (back) back.focus();
    }

    closeSubpage() {
      if (this.activePanel === null) return;

      var closingIndex = this.activePanel;
      this.activePanel = null;

      this.subPanels.forEach((panel) => {
        panel.hidden = true;
      });
      if (this.rootPanel) this.rootPanel.hidden = false;

      var opener = this.drawer.querySelector('[data-subpage-open="' + closingIndex + '"]');
      if (opener) {
        opener.setAttribute('aria-expanded', 'false');
        if (this.drawerOpen) opener.focus();
      }
    }

    trapFocus(event) {
      var focusable = Array.from(this.drawer.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (!focusable.length) return;

      // 汉堡按钮本身也留在环里，方便用键盘退回去关闭
      if (this.drawerToggle) focusable.unshift(this.drawerToggle);

      var firstEl = focusable[0];
      var lastEl = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    }

    openFlyout(index) {
      if (index === undefined || index === null) return;
      if (this.activeIndex === index) return;

      this.cancelFlyoutClose();
      this.activeIndex = index;

      this.flyouts.forEach((panel) => {
        panel.hidden = panel.dataset.flyoutFor !== index;
      });

      this.triggers.forEach((item) => {
        var link = item.querySelector('.header-nav__link');
        if (link) link.setAttribute('aria-expanded', item.dataset.itemIndex === index ? 'true' : 'false');
      });

      this.setAttribute('data-flyout-open', 'true');
    }

    closeFlyout() {
      this.cancelFlyoutClose();
      if (this.activeIndex === null) {
        this.setAttribute('data-flyout-open', 'false');
        return;
      }

      this.activeIndex = null;

      this.flyouts.forEach((panel) => {
        panel.hidden = true;
      });

      this.triggers.forEach((item) => {
        var link = item.querySelector('.header-nav__link');
        if (link) link.setAttribute('aria-expanded', 'false');
      });

      this.setAttribute('data-flyout-open', 'false');
    }

    scheduleFlyoutClose() {
      this.cancelFlyoutClose();
      this.flyoutCloseTimer = window.setTimeout(() => {
        var barHovered = this.bar && this.bar.matches(':hover');
        var flyoutHovered = this.flyoutRegion && this.flyoutRegion.matches(':hover');
        if (!barHovered && !flyoutHovered) this.closeFlyout();
      }, 120);
    }

    cancelFlyoutClose() {
      if (this.flyoutCloseTimer === null) return;

      window.clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = null;
    }
  }

  if (!customElements.get('header-nav')) {
    customElements.define('header-nav', HeaderNav);
  }
})();
